/**
 * Phase 15 Plan 15-02 — MonthPicker
 *
 * Reusable year + month selector that emits a "YYYY-MM" string.
 * Built from two shadcn <Select> controls — no external date library required.
 *
 * Props:
 *   value    — current "YYYY-MM" string
 *   onChange — callback with new "YYYY-MM" string
 *   disabled — disables both selects
 *   min      — minimum selectable "YYYY-MM" (inclusive)
 *   max      — maximum selectable "YYYY-MM" (inclusive)
 */

import type { JSX } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const MONTH_LABELS: readonly string[] = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const START_YEAR = 2024;

function currentYear(): number {
  return new Date().getFullYear();
}

function buildYears(): readonly number[] {
  const end = currentYear() + 1;
  const years: number[] = [];
  for (let y = START_YEAR; y <= end; y++) {
    years.push(y);
  }
  return years;
}

export interface MonthPickerProps {
  readonly value: string;           // "YYYY-MM"
  readonly onChange: (next: string) => void;
  readonly disabled?: boolean;
  readonly min?: string;            // "YYYY-MM" inclusive lower bound
  readonly max?: string;            // "YYYY-MM" inclusive upper bound
}

export function MonthPicker({ value, onChange, disabled, min, max }: MonthPickerProps): JSX.Element {
  const [yearStr, monthStr] = value.split('-');
  const selectedYear = yearStr !== undefined && yearStr !== '' ? Number(yearStr) : currentYear();
  const selectedMonth =
    monthStr !== undefined && monthStr !== '' ? Number(monthStr) : new Date().getMonth() + 1;

  const years = buildYears();

  function handleYearChange(newYearStr: string): void {
    const newYear = Number(newYearStr);
    const newValue = `${String(newYear)}-${String(selectedMonth).padStart(2, '0')}`;
    if (min !== undefined && newValue < min) return;
    if (max !== undefined && newValue > max) return;
    onChange(newValue);
  }

  function handleMonthChange(newMonthStr: string): void {
    const newMonth = Number(newMonthStr);
    const newValue = `${String(selectedYear)}-${String(newMonth).padStart(2, '0')}`;
    if (min !== undefined && newValue < min) return;
    if (max !== undefined && newValue > max) return;
    onChange(newValue);
  }

  return (
    <div className="flex items-center gap-2" data-testid="month-picker">
      {/* Year selector */}
      <Select
        value={String(selectedYear)}
        onValueChange={handleYearChange}
        disabled={disabled === true}
      >
        <SelectTrigger
          className="w-[90px]"
          data-testid="month-picker-year"
          aria-label="Select year"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {years.map((y) => (
            <SelectItem
              key={y}
              value={String(y)}
              data-testid={`month-picker-year-${String(y)}`}
            >
              {String(y)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Month selector */}
      <Select
        value={String(selectedMonth)}
        onValueChange={handleMonthChange}
        disabled={disabled === true}
      >
        <SelectTrigger
          className="w-[130px]"
          data-testid="month-picker-month"
          aria-label="Select month"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {MONTH_LABELS.map((label, idx) => {
            const monthNum = idx + 1;
            return (
              <SelectItem
                key={monthNum}
                value={String(monthNum)}
                data-testid={`month-picker-month-${String(monthNum)}`}
              >
                {label}
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}
